import React from "react";
import {
  Send,
  Trash2,
  CheckCircle,
  MessageSquare,
  Bot,
  Users,
  RefreshCw,
  AlertCircle,
  RotateCcw,
  Truck,
  Home,
  Box,
  Clock,
  Globe,
} from "lucide-react";
import { formatCurrency } from "../utils/formatCurrency";
import { isOnlineOrder, onlineOrderLabel } from "../utils/orderSource";

/**
 * ReceiptCard v4 — Full-width row with all original card elements restored.
 *
 * Layout: 9-column CSS grid, ~88px tall.
 *
 *   ┌─────────────────────────────────────────────────────────────────────────┐
 *   │ [Status pill] │ Customer │ ITEMS │ BALANCE │ SMS │ Total/Date │ Toggle │
 *   │               │  info    │ [box] │  [box]  │[box]│            │ + btns │
 *   └─────────────────────────────────────────────────────────────────────────┘
 *
 * Why this layout:
 * - Same column structure for every row → zoom and screen size can't break it.
 * - Stat boxes (ITEMS / BALANCE / SMS) restored from the original card so each
 *   piece of info has a defined "slot" your eye scans across.
 * - Status pill on the left mirrors the original card's prominent green badge.
 * - Border color encodes status at-a-glance (emerald = ready, blue = partial,
 *   slate = delivered, red = escalation, blue = unread).
 * - All numbers are tabular-nums so totals/balances align column-down.
 *
 * Design principles:
 * - 1.5px colored border instead of stacked rings — single visual anchor.
 * - Box stats use light tinted backgrounds for color-coded status without
 *   needing to read the text.
 * - Salesperson and date kept on subtitle line so customer column stays compact.
 */
const ReceiptCard = ({
  receipt,
  // state
  sendingSMSReceiptId,
  smsSentReceipts,
  togglingChatModeId,
  unreadMessages = {},
  escalations = {},
  allInventoryUnits,
  // helpers
  normalizeStatus,
  getItemReadinessByQuantity,
  getPaymentStatus,
  // handlers
  handleOpenReceipt,
  handleSendSMS,
  handleDeleteReceipt,
  handleToggleChatMode = () => {},
}) => {
  const status = normalizeStatus(
    receipt.deliveryStatus || receipt.delivery_status,
  );
  const customerName =
    receipt.customerName || receipt.customer_name || "Unnamed customer";
  const customerPhone =
    receipt.customerPhone || receipt.customer_phone || "";
  const createdAt = receipt.createdAt || receipt.created_at;
  const createdDate = createdAt
    ? new Date(createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "—";
  const sentAt = receipt.sentAt || receipt.sent_at;

  const readiness = getItemReadinessByQuantity(receipt, allInventoryUnits);
  const {
    totalQty,
    deliveredQty,
    pickedUpQty,
    readyQty,
    outForDeliveryQty,
    waitingQty,
    allReady,
  } = readiness;

  const totalItems = totalQty;
  const deliveredCount = (deliveredQty || 0) - (pickedUpQty || 0);
  const pickedUpCount = pickedUpQty || 0;
  const readyCount = readyQty || 0;
  const outForDeliveryCount = outForDeliveryQty || 0;
  const waitingCount = waitingQty || 0;
  const totalCompleted = deliveredCount + pickedUpCount;

  const allOutForDelivery =
    totalItems > 0 && outForDeliveryCount === totalItems;
  const isOutForDelivery =
    status === "out_for_delivery" || allOutForDelivery;
  const isPartialOutForDelivery =
    status === "partial_out_for_delivery" ||
    (outForDeliveryCount > 0 && outForDeliveryCount < totalItems);

  const paymentInfo = getPaymentStatus(receipt);

  const unreadCount = unreadMessages[receipt.id] || 0;
  const hasUnread =
    unreadCount > 0 ||
    (receipt.hasActiveChat && receipt.unreadMessages > 0);
  const displayUnread = unreadCount || receipt.unreadMessages || 0;

  const escalation = escalations[receipt.id];
  const hasEscalation = escalation && !escalation.acknowledged;

  /* ─ Compute primary status pill ────────────────────────────────────── */
  const primaryStatus = (() => {
    const isFullyComplete =
      status === "delivered" ||
      status === "picked_up" ||
      (totalCompleted === totalItems && totalItems > 0);
    if (isFullyComplete) {
      // Label by how the items actually left: all picked up → "Picked Up",
      // all delivered → "Delivered", a mix of both doors → "Complete".
      const allPickedUp =
        status === "picked_up" || (pickedUpCount > 0 && deliveredCount === 0);
      const mixed = pickedUpCount > 0 && deliveredCount > 0;
      if (allPickedUp && !mixed) {
        return {
          label: "Picked Up",
          Icon: Box,
          cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
          border: "border-emerald-200",
        };
      }
      return {
        label: mixed ? "Complete" : "Delivered",
        Icon: Home,
        cls: "bg-slate-100 text-slate-700 border-slate-300",
        border: "border-slate-300",
      };
    }
    if (isOutForDelivery) {
      return {
        label: "Out for Delivery",
        Icon: Truck,
        cls: "bg-cyan-50 text-cyan-700 border-cyan-200",
        border: "border-cyan-200",
      };
    }
    if (isPartialOutForDelivery) {
      return {
        label: `${outForDeliveryCount} of ${totalItems} on Truck`,
        Icon: Truck,
        cls: "bg-blue-50 text-blue-700 border-blue-200",
        border: "border-blue-200",
      };
    }
    // All remaining (not-yet-completed) items are ready and nothing is
    // waiting or on a truck → the order is ready to go. This must win over
    // the partial-complete branch below: a receipt with some items picked
    // up and every other item ready is "Ready to Ship", not "partial".
    // (Mirrors the mobile card, which lights green in exactly this case.)
    if (allReady) {
      return {
        label: "Ready to Ship",
        Icon: CheckCircle,
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        border: "border-emerald-300",
      };
    }
    if (totalCompleted > 0 && totalCompleted < totalItems) {
      return {
        label: `${totalCompleted} of ${totalItems} ${
          pickedUpCount > 0
            ? deliveredCount > 0
              ? "Complete"
              : "Picked Up"
            : "Delivered"
        }`,
        Icon: Home,
        cls: "bg-amber-50 text-amber-700 border-amber-200",
        border: "border-amber-200",
      };
    }
    if (readyCount > 0) {
      return {
        label: `${readyCount} of ${totalItems} Ready`,
        Icon: CheckCircle,
        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
        border: "border-emerald-200",
      };
    }
    return {
      label: "Pending",
      Icon: Clock,
      cls: "bg-slate-50 text-slate-600 border-slate-200",
      border: "border-slate-200",
    };
  })();

  /* ─ Online order (came in from the store's website, not the floor) ── */
  const isOnline = isOnlineOrder(receipt);
  const onlineLabel = onlineOrderLabel(receipt);

  /* ─ Border treatment ───────────────────────────────────────────────── */
  const rowBorder = hasEscalation
    ? "border-red-300 hover:border-red-400"
    : hasUnread
      ? "border-blue-300 hover:border-blue-400"
      : isOnline
        ? "border-violet-400 hover:border-violet-500"
        : `${primaryStatus.border}`;

  /* ─ Balance stat box ───────────────────────────────────────────────── */
  const balanceBox = (() => {
    if (paymentInfo.status === "paid") {
      return {
        label: "Paid",
        cls: "bg-emerald-50 border-emerald-200",
        textCls: "text-emerald-700",
      };
    }
    if (paymentInfo.status === "partial") {
      return {
        label: `$${formatCurrency(paymentInfo.balance, { decimals: 0 })}`,
        cls: "bg-amber-50 border-amber-200",
        textCls: "text-amber-700",
      };
    }
    return {
      label: "Unpaid",
      cls: "bg-red-50 border-red-200",
      textCls: "text-red-600",
    };
  })();

  /* ─ SMS stat box ───────────────────────────────────────────────────── */
  const smsBox = (() => {
    if (receipt.sms_opted_out) {
      return {
        label: "Opt-out",
        cls: "bg-red-50 border-red-200",
        textCls: "text-red-600",
      };
    }
    if (sentAt) {
      return {
        label: "Sent",
        cls: "bg-emerald-50 border-emerald-200",
        textCls: "text-emerald-700",
      };
    }
    return {
      label: "—",
      cls: "bg-white border-slate-200",
      textCls: "text-slate-300",
    };
  })();

  return (
    <div
      onClick={() => handleOpenReceipt(receipt)}
      className={`relative bg-white rounded-xl border-[1.5px] ${rowBorder} shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px transition-all cursor-pointer group grid items-center gap-3 lg:gap-4 px-4 lg:px-5 py-3.5`}
      style={{
        // Columns: status pill | customer | items | balance | sms | total/date | spacer | toggle | resend | delete
        gridTemplateColumns:
          "minmax(140px, 180px) minmax(160px, 1.4fr) 80px 96px 84px minmax(110px, auto) 1fr 108px 108px 36px",
        minHeight: "84px",
      }}
    >
      {/* Online order ribbon: big, top-right, so it stands out on the row */}
      {isOnline && (
        <div className="absolute -top-3 right-4 flex items-center gap-1.5 bg-violet-600 text-white pl-2.5 pr-3 py-1 rounded-full shadow-md ring-2 ring-white z-10">
          <Globe className="w-3.5 h-3.5" />
          <span className="text-[12px] font-black tracking-wide uppercase whitespace-nowrap">{onlineLabel}</span>
        </div>
      )}

      {/* Corner badges */}
      {hasEscalation && (
        <div className="absolute -top-2 left-3 flex items-center gap-1 bg-red-500 text-white px-2 py-0.5 rounded-full shadow-sm z-10">
          <AlertCircle className="w-3 h-3" />
          <span className="text-[10px] font-semibold tracking-wide uppercase">Urgent</span>
        </div>
      )}
      {hasUnread && !hasEscalation && (
        <div className="absolute -top-2 left-3 flex items-center gap-1 bg-blue-500 text-white px-2 py-0.5 rounded-full shadow-sm z-10">
          <MessageSquare className="w-3 h-3" />
          <span className="text-[10px] font-semibold">{displayUnread} new</span>
        </div>
      )}

      {/* ── Col 1: Status pill ────────────────────────────────────────── */}
      <div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-semibold whitespace-nowrap ${primaryStatus.cls}`}
        >
          <primaryStatus.Icon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{primaryStatus.label}</span>
        </span>
      </div>

      {/* ── Col 2: Customer ───────────────────────────────────────────── */}
      <div className="min-w-0">
        <h3 className="text-[17px] font-black text-slate-900 group-hover:text-blue-600 transition-colors truncate tracking-tight leading-tight">
          {customerName}
        </h3>
        {isOnline && (
          <p className="text-[12px] font-bold text-violet-700 mt-0.5 truncate">
            <Globe className="w-3 h-3 inline-block -mt-0.5 mr-1" />
            {onlineLabel}
          </p>
        )}
        <p className="text-[11px] text-slate-500 mt-1 truncate tabular-nums">
          {customerPhone}
          {receipt.salesperson && (
            <span className="text-slate-400"> · Sold by {receipt.salesperson}</span>
          )}
        </p>

        {/* Item status breakdown — full picture, mirrors mobile card */}
        {totalItems > 0 && (
          <div className="flex items-center flex-wrap gap-1 mt-1.5">
            {deliveredCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-300">
                <Home className="w-2.5 h-2.5 text-emerald-600" />
                <span className="text-[10px] font-semibold text-emerald-700 leading-none">
                  {deliveredCount} Delivered
                </span>
              </span>
            )}
            {pickedUpCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-300">
                <Box className="w-2.5 h-2.5 text-emerald-600" />
                <span className="text-[10px] font-semibold text-emerald-700 leading-none">
                  {pickedUpCount} Picked Up
                </span>
              </span>
            )}
            {outForDeliveryCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-cyan-100 border border-cyan-300">
                <Truck className="w-2.5 h-2.5 text-cyan-600" />
                <span className="text-[10px] font-semibold text-cyan-700 leading-none">
                  {outForDeliveryCount} On Truck
                </span>
              </span>
            )}
            {readyCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 border border-green-300">
                <CheckCircle className="w-2.5 h-2.5 text-green-600" />
                <span className="text-[10px] font-semibold text-green-700 leading-none">
                  {readyCount} Ready
                </span>
              </span>
            )}
            {waitingCount > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-300">
                <Clock className="w-2.5 h-2.5 text-slate-500" />
                <span className="text-[10px] font-semibold text-slate-600 leading-none">
                  {waitingCount} Waiting
                </span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Col 3: Items box ──────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-center">
        <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider leading-none">
          Items
        </div>
        <div className="text-[14px] font-semibold text-slate-900 mt-1 leading-none tabular-nums">
          {totalItems}
        </div>
      </div>

      {/* ── Col 4: Balance box ────────────────────────────────────────── */}
      <div className={`border rounded-lg px-2 py-1.5 text-center ${balanceBox.cls}`}>
        <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider leading-none">
          Balance
        </div>
        <div className={`text-[13px] font-semibold mt-1 leading-none tabular-nums truncate ${balanceBox.textCls}`}>
          {balanceBox.label}
        </div>
      </div>

      {/* ── Col 5: SMS box ────────────────────────────────────────────── */}
      <div className={`border rounded-lg px-2 py-1.5 text-center ${smsBox.cls}`}>
        <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider leading-none">
          SMS
        </div>
        <div className={`text-[13px] font-semibold mt-1 leading-none ${smsBox.textCls}`}>
          {smsBox.label}
        </div>
      </div>

      {/* ── Col 6: Total + date ───────────────────────────────────────── */}
      <div className="text-right">
        <div className="text-[20px] font-semibold text-slate-900 leading-none tabular-nums tracking-tight">
          ${formatCurrency(receipt.total)}
        </div>
        <div className="text-[11px] text-slate-400 tabular-nums mt-1.5">
          {createdDate}
        </div>
      </div>

      {/* ── Col 7: Spacer ─────────────────────────────────────────────── */}
      <div />

      {/* ── Col 8: AI/You toggle ──────────────────────────────────────── */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleToggleChatMode(receipt, e);
        }}
        disabled={togglingChatModeId === receipt.id}
        title={
          receipt.is_ai_handling !== false
            ? "AI is auto-replying. Click to take over manually."
            : "You're replying manually. Click to hand back to AI."
        }
        className="flex items-center p-0.5 rounded-full bg-slate-50 border border-slate-200 transition-all disabled:opacity-60 hover:border-slate-300 justify-self-center"
      >
        {togglingChatModeId === receipt.id ? (
          <div className="flex items-center gap-1 px-2.5 py-1">
            <RefreshCw className="w-3 h-3 animate-spin text-slate-500" />
            <span className="text-[11px] font-medium text-slate-500">…</span>
          </div>
        ) : (
          <>
            <span
              className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all ${
                receipt.is_ai_handling !== false
                  ? "bg-violet-600 text-white"
                  : "text-slate-400"
              }`}
            >
              <Bot className="w-3 h-3" />
              <span className="text-[11px] font-semibold">AI</span>
            </span>
            <span
              className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all ${
                receipt.is_ai_handling === false
                  ? "bg-amber-500 text-white"
                  : "text-slate-400"
              }`}
            >
              <Users className="w-3 h-3" />
              <span className="text-[11px] font-semibold">You</span>
            </span>
          </>
        )}
      </button>

      {/* ── Col 9: Send / Resend button ───────────────────────────────── */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleSendSMS(receipt);
        }}
        disabled={sendingSMSReceiptId === receipt.id || receipt.sms_opted_out}
        title={
          receipt.sms_opted_out
            ? "Customer opted out — cannot send SMS"
            : sentAt
              ? "Resend Receipt"
              : "Send Receipt"
        }
        className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-[13px] font-semibold whitespace-nowrap transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
          receipt.sms_opted_out
            ? "bg-red-50 border-red-200 text-red-600"
            : smsSentReceipts[receipt.id]
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : sentAt
                ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
        }`}
      >
        {receipt.sms_opted_out ? (
          <>
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Opt-out</span>
          </>
        ) : sendingSMSReceiptId === receipt.id ? (
          <>
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span>Sending</span>
          </>
        ) : smsSentReceipts[receipt.id] ? (
          <>
            <CheckCircle className="w-3.5 h-3.5" />
            <span>Sent</span>
          </>
        ) : sentAt ? (
          <>
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Resend</span>
          </>
        ) : (
          <>
            <Send className="w-3.5 h-3.5" />
            <span>Send</span>
          </>
        )}
      </button>

      {/* ── Col 10: Delete ────────────────────────────────────────────── */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteReceipt(receipt.id);
        }}
        title="Delete receipt"
        className="flex items-center justify-center w-9 h-9 rounded-md border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default ReceiptCard;